import { getSessionAuthorizationHeaders } from '../../services/sessionAuth';
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { Message } from '../../models/Message';
import type {
  ConversationMode,
} from '../../services/conversationPersistence';
import {
  LANG_LABEL,
  TARGET_LANG,
} from '../../constants';
import { ReportProblemDialog } from '../ReportProblemDialog';
import RetziaRichMessage from './RetziaRichMessage';

import retziaAvatarV13Url from '../../assets/retzia-avatar-v13.png';

import './RetziaActivityWorkspace.css';

export interface RetziaActivitySession {
  messages: Message[];
  input: string;
  showSendIcon: boolean;
  isPauseStarted: boolean;
  isAudioPaused: boolean;
  errorAppeared: boolean;
  isMicActivated: boolean;
  messagesEndRef: {
    current: HTMLDivElement | null;
  };
  isRecording: boolean;
  isWaiting: boolean;
  isBotSpeaking: boolean;
  isWaitingForBot: boolean;
  handleChange: (
    event:
      React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement
      >
  ) => void;
  handleSubmit: (
    content: string
  ) => void | Promise<void>;
  activateMic: () => void;
  sendInput: () => void;
  cancelMicFlow: () => void;
  confirmSendAudio: () => void | Promise<void>;
  resumeAudioPaused: () => void;
  pauseAudio: () => void | Promise<void>;
  cancelAudioFlow: () => void | Promise<void>;
  editLastMessage: (
    content: string
  ) => void | Promise<void>;
  playBotMessageAudio: (
    index: number,
    content: string
  ) => void | Promise<void>;
  playUserMessageAudio: (
    index: number
  ) => void;
  userAudioIndices: Set<number>;
  getUserAudioUrl: (
    index: number
  ) => string | undefined;
  fetchSuggestion: (
    prompt?: string
  ) => Promise<string | null>;
  evictBotAudioCache: (
    index: number
  ) => void;
  audioConfirmed: boolean;
  stopBotSpeaking: () => void;
  playingIndex: number | null;
  stopCurrentPlayback: () => void;
  pausedPlaybackIndex?: number | null;
  pauseCurrentPlayback?: () => void;
  resumeCurrentPlayback?: () => void;
  changeTopic?: (
    topic: string,
    customContent?: string
  ) => void | Promise<void>;
}

interface RetziaActivityWorkspaceProps {
  newConversationLabel?: string;
  onNewConversation?: () => void;
  newConversationDisabled?: boolean;
  persistenceError?: string;
  language: string;
  session: RetziaActivitySession;
  reportMode: ConversationMode;
  hideFirstMessage?: boolean;
  header?: React.ReactNode;
  afterConversation?: React.ReactNode;
  pageClassName?: string;
  inputPlaceholder?: string;
  topics?: string[];
  topicsLayout?: 'sheet' | 'rail';
  topicPrompt?: (
    topic: string
  ) => string;
  onClose: () => void;
}



const Ca13TranslateIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 5h10M9 3v2m-3 4c1.2 2 3.5 4.5 7 6M12 9c-1 2.2-3.4 4.8-7 6m10 4 3-7 3 7m-5-2h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Ca13SpeakerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 10v4h3l4 3V7L8 10H5Zm10-1.5c1.8 1.7 1.8 5.3 0 7m2.5-9.5c3.4 3.2 3.4 8.8 0 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Ca13PauseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 7v10M15 7v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Ca13PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="m9 6 9 6-9 6V6Z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
  </svg>
);

const Ca13StopIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="7" y="7" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.9" />
  </svg>
);

const Ca13EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 20h4l10-10-4-4L4 16v4Zm9-13 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Ca13DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 4v10m0 0 4-4m-4 4-4-4M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Ca13CloseIcon = ({ size = 17 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Ca13KeyboardIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M6 10h1m2 0h1m2 0h1m2 0h1m2 0h1M6 14h9m2 0h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Ca13BackIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 5 8 12l7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Ca13BulbIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 18h6m-5 3h4m5-10a7 7 0 1 0-11.5 5.4c.9.7 1.5 1.4 1.7 2.6h5.6c.2-1.2.8-1.9 1.7-2.6A6.9 6.9 0 0 0 19 11Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

const Ca13GlobeIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
    <path d="M3 12h18M12 3c2.3 2.5 3.4 5.5 3.4 9S14.3 18.5 12 21M12 3C9.7 5.5 8.6 8.5 8.6 12S9.7 18.5 12 21" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const Ca13HashIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 3 7 21M17 3l-2 18M4 9h16M3 15h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Ca13MicIcon = ({ size = 26 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2" />
    <path d="M6.5 11.5a5.5 5.5 0 0 0 11 0M12 17v4m-3 0h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Ca13SendIcon = ({ size = 19 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 12 20 4l-5 16-3-6-8-2Zm8 2 8-10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Ca13ArrowIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14M14 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Ca13TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 7h12M9 7V4h6v3m-8 0 1 13h8l1-13M10 10v7m4-7v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Ca13RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 7v5h-5M4 17v-5h5M18 10a7 7 0 0 0-12-3l-2 2m2 5a7 7 0 0 0 12 3l2-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Ca13RecordingPauseIcon = ({ paused }: { paused: boolean }) => (
  paused ? (
    <Ca13MicIcon size={20} />
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M10 9v6m4-6v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
);


function RetziaActivityWorkspace({
  newConversationLabel, onNewConversation, newConversationDisabled, persistenceError,
  language,
  session,
  reportMode,
  hideFirstMessage = false,
  header,
  afterConversation,
  pageClassName,
  inputPlaceholder = 'Escribe aquí…',
  topics = [],
  topicsLayout = 'sheet',
  topicPrompt,
  onClose,
}: RetziaActivityWorkspaceProps) {
  const {
    messages,
    input,
    showSendIcon,
    isAudioPaused,
    errorAppeared,
    isMicActivated,
    messagesEndRef,
    isRecording,
    isWaiting,
    isBotSpeaking,
    isWaitingForBot,
    handleChange,
    handleSubmit,
    activateMic,
    sendInput,
    cancelMicFlow,
    confirmSendAudio,
    resumeAudioPaused,
    pauseAudio,
    cancelAudioFlow,
    editLastMessage,
    playBotMessageAudio,
    playUserMessageAudio,
    userAudioIndices,
    getUserAudioUrl,
    fetchSuggestion,
    evictBotAudioCache,
    audioConfirmed,
    stopBotSpeaking,
    playingIndex,
    stopCurrentPlayback,
    pausedPlaybackIndex = null,
    pauseCurrentPlayback,
    resumeCurrentPlayback,
    changeTopic,
  } = session;

  const [mobileInputOpen, setMobileInputOpen] =
    useState(false);

  const [ca13HeaderScrolled, setCa13HeaderScrolled] =
    useState(false);

  const [ca13RecordingSeconds, setCa13RecordingSeconds] =
    useState(0);

  const [showTopics, setShowTopics] =
    useState(false);

  const [topicMarkers, setTopicMarkers] =
    useState<Array<{
      afterIndex: number;
      label: string;
    }>>([]);

  const hasTopics =
    topics.length > 0 &&
    Boolean(changeTopic);

  const hasTopicSheet =
    hasTopics && topicsLayout === 'sheet';

  const hasTopicRail =
    hasTopics && topicsLayout === 'rail';

  const ca13TranslatorInputRef =
    useRef<HTMLTextAreaElement>(null);

  const inputRef =
    useRef<HTMLInputElement>(null);

  const [
    transcribedThisSession,
    setTranscribedThisSession,
  ] = useState(false);

  const [isEditing, setIsEditing] =
    useState(false);

  const [
    editingMessageText,
    setEditingMessageText,
  ] = useState('');

  const [
    translatedIndex,
    setTranslatedIndex,
  ] = useState<number | null>(null);

  const [
    translationText,
    setTranslationText,
  ] = useState('');

  const [
    isTranslating,
    setIsTranslating,
  ] = useState(false);

  const translationRef =
    useRef<HTMLDivElement>(null);

  const [bulbIsLit, setBulbIsLit] =
    useState(false);

  const bulbTimerRef =
    useRef<ReturnType<typeof setTimeout> | null>(
      null
    );

  const [
    showSuggestion,
    setShowSuggestion,
  ] = useState(false);

  const [
    suggestionText,
    setSuggestionText,
  ] = useState('');

  const [
    isLoadingSuggestion,
    setIsLoadingSuggestion,
  ] = useState(false);

  const [
    showSuggestionTranslation,
    setShowSuggestionTranslation,
  ] = useState(false);

  const [
    suggestionTranslation,
    setSuggestionTranslation,
  ] = useState('');

  const [
    isTranslatingSuggestion,
    setIsTranslatingSuggestion,
  ] = useState(false);

  const suggestionTranslationRef =
    useRef<HTMLDivElement>(null);

  const [
    showTranslator,
    setShowTranslator,
  ] = useState(false);

  const [
    translatorInput,
    setTranslatorInput,
  ] = useState('');

  const [
    translatorResult,
    setTranslatorResult,
  ] = useState('');

  const [
    isTranslatorLoading,
    setIsTranslatorLoading,
  ] = useState(false);

  const [
    isTranslatorDiloActive,
    setIsTranslatorDiloActive,
  ] = useState(false);

  useEffect(() => {
    if (!isWaiting) {
      setTranscribedThisSession(false);
    } else if (
      messages[
        messages.length - 1
      ]?.role === 'user'
    ) {
      setTranscribedThisSession(true);
    }
  }, [isWaiting, messages]);

  useEffect(() => {
    if (isRecording) {
      setTimeout(
        () =>
          messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth',
          }),
        50
      );
    }
    // Same scroll behavior as TaskReview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  useEffect(() => {
    if (
      isWaiting &&
      audioConfirmed &&
      !transcribedThisSession
    ) {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isWaiting,
    transcribedThisSession,
    audioConfirmed,
  ]);

  useEffect(() => {
    if (isWaitingForBot) {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWaitingForBot]);

  useEffect(() => {
    if (translatedIndex !== null) {
      setTimeout(
        () =>
          translationRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          }),
        50
      );
    }
  }, [
    translatedIndex,
    isTranslating,
  ]);

  useEffect(() => {
    if (showSuggestion) {
      setTimeout(
        () =>
          messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth',
          }),
        50
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuggestion]);

  useEffect(() => {
    if (showSuggestionTranslation) {
      setTimeout(
        () =>
          suggestionTranslationRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          }),
        50
      );
    }
  }, [
    showSuggestionTranslation,
    isTranslatingSuggestion,
  ]);

  useEffect(() => {
    if (bulbTimerRef.current) {
      clearTimeout(
        bulbTimerRef.current
      );
    }

    setBulbIsLit(false);

    const visibleMessages =
      hideFirstMessage
        ? messages.slice(1)
        : messages;

    const last =
      visibleMessages[
        visibleMessages.length - 1
      ];

    if (
      !last ||
      last.role !== 'assistant' ||
      visibleMessages.length < 3 ||
      isMicActivated
    ) {
      return;
    }

    bulbTimerRef.current =
      setTimeout(
        () => setBulbIsLit(true),
        5000
      );

    return () => {
      if (bulbTimerRef.current) {
        clearTimeout(
          bulbTimerRef.current
        );
      }
    };
  }, [
    messages,
    isMicActivated,
    hideFirstMessage,
  ]);

  useEffect(() => {
    if (!isRecording) {
      setIsTranslatorDiloActive(false);
    }
  }, [isRecording]);

  useEffect(() => {
    if (!isRecording) {
      setCa13RecordingSeconds(0);
      return;
    }

    if (isAudioPaused) {
      return;
    }

    const timer = window.setInterval(() => {
      setCa13RecordingSeconds(
        seconds => seconds + 1
      );
    }, 1000);

    return () => window.clearInterval(timer);
  }, [
    isRecording,
    isAudioPaused,
  ]);

  const openMobileInput = () => {
    setMobileInputOpen(true);

    setTimeout(
      () => inputRef.current?.focus(),
      50
    );
  };

  const stopBulbBlink = () => {
    if (bulbTimerRef.current) {
      clearTimeout(
        bulbTimerRef.current
      );

      bulbTimerRef.current = null;
    }

    setBulbIsLit(false);
  };

  const closeSuggestion = () => {
    setShowSuggestion(false);
    setSuggestionText('');
    setShowSuggestionTranslation(false);
    setSuggestionTranslation('');
    stopBulbBlink();
  };

  const handleConfirmAudio = () => {
    closeSuggestion();
    void confirmSendAudio();
  };

  const fetchAndShowSuggestion =
    async () => {
      if (isWaitingForBot) {
        return;
      }

      evictBotAudioCache(-1);
      stopBulbBlink();

      setShowSuggestion(true);
      setIsLoadingSuggestion(true);
      setSuggestionText('');
      setShowSuggestionTranslation(false);
      setSuggestionTranslation('');

      const text = await fetchSuggestion(
        'You are a language learner being asked the last message as a question. Answer it naturally and briefly. Reply with ONLY the suggested text, no explanation.'
      );

      setSuggestionText(
        text ?? 'No se pudo generar la sugerencia. Toca ↻ para reintentar.'
      );
      setIsLoadingSuggestion(false);
    };

  const handleRefreshSuggestion =
    async () => {
      if (isWaitingForBot) {
        return;
      }

      evictBotAudioCache(-1);

      setIsLoadingSuggestion(true);
      setSuggestionText('');
      setShowSuggestionTranslation(false);
      setSuggestionTranslation('');

      const text = await fetchSuggestion(
        'You are a language learner being asked the last message as a question. Answer it naturally and briefly. Reply with ONLY the suggested text, no explanation.'
      );

      setSuggestionText(
        text ?? 'No se pudo generar la sugerencia. Toca ↻ para reintentar.'
      );
      setIsLoadingSuggestion(false);
    };

  const handleSuggestionTranslate =
    async () => {
      if (showSuggestionTranslation) {
        setShowSuggestionTranslation(false);
        setSuggestionTranslation('');
        return;
      }

      setShowSuggestionTranslation(true);
      setSuggestionTranslation('');
      setIsTranslatingSuggestion(true);

      try {
        const response = await fetch(
          '/translate.php',
          {
            method: 'POST',
            headers: {
              ...getSessionAuthorizationHeaders(),
              'Content-Type':
                'application/json',
            },
            body: JSON.stringify({
              text: suggestionText,
            }),
          }
        );

        if (!response.ok) throw new Error('Translation failed');
        const data = await response.json();

        setSuggestionTranslation(
          data.translations[0].text
        );
      } catch {
        setShowSuggestionTranslation(false);
      } finally {
        setIsTranslatingSuggestion(false);
      }
    };

  const handleSuggestionPlay = () => {
    if (
      !suggestionText ||
      isLoadingSuggestion
    ) {
      return;
    }

    void playBotMessageAudio(
      -1,
      suggestionText
    );
  };

  const handleTranslate = async (
    index: number,
    content: string
  ) => {
    closeSuggestion();

    if (translatedIndex === index) {
      setTranslatedIndex(null);
      setTranslationText('');
      return;
    }

    setTranslatedIndex(index);
    setTranslationText('');
    setIsTranslating(true);

    try {
      const response = await fetch(
        '/translate.php',
        {
          method: 'POST',
          headers: {
              ...getSessionAuthorizationHeaders(),
            'Content-Type':
              'application/json',
          },
          body: JSON.stringify({
            text: content,
          }),
        }
      );

      if (!response.ok) throw new Error('Translation failed');
        const data = await response.json();

      setTranslationText(
        data.translations[0].text
      );
    } catch {
      setTranslatedIndex(null);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleStartEdit = (
    content: string
  ) => {
    setIsEditing(true);
    setEditingMessageText(content);

    handleChange({
      target: {
        value: content,
      },
    } as React.ChangeEvent<HTMLInputElement>);

    setMobileInputOpen(true);

    setTimeout(
      () => inputRef.current?.focus(),
      50
    );
  };

  const handleSend = () => {
    if (isWaitingForBot) {
      return;
    }

    closeSuggestion();

    if (isEditing) {
      void editLastMessage(input);

      setIsEditing(false);
      setEditingMessageText('');
    } else {
      sendInput();
    }

    setMobileInputOpen(false);
  };

  const toggleTopics = () => {
    if (!hasTopics) {
      return;
    }

    closeSuggestion();
    setShowTranslator(false);
    setShowTopics(previous => !previous);
  };

  const closeTopics = () => {
    setShowTopics(false);
  };

  const handleTopicSelect = (
    topic: string
  ) => {
    if (!changeTopic || isWaitingForBot) {
      return;
    }

    closeSuggestion();
    closeTopics();

    setTopicMarkers(previous => [
      ...previous,
      {
        afterIndex: messages.length,
        label: topic,
      },
    ]);

    void changeTopic(
      topic,
      topicPrompt?.(topic)
    );
  };

  const openTranslator = () => {
    closeSuggestion();
    setShowTopics(false);
    setShowTranslator(true);

    setTimeout(() => {
      ca13TranslatorInputRef.current?.focus();
    }, 60);
  };

  const closeTranslator = () => {
    setShowTranslator(false);
    setTranslatorInput('');
    setTranslatorResult('');
    setIsTranslatorDiloActive(false);
  };

  const handleTranslatorSubmit =
    async () => {
      if (
        !translatorInput.trim() ||
        isTranslatorLoading
      ) {
        return;
      }

      evictBotAudioCache(-2);

      const textToTranslate =
        translatorInput;

      setIsTranslatorLoading(true);
      setTranslatorResult('');

      try {
        const response = await fetch(
          '/translate.php',
          {
            method: 'POST',
            headers: {
              ...getSessionAuthorizationHeaders(),
              'Content-Type':
                'application/json',
            },
            body: JSON.stringify({
              text: textToTranslate,
              target_lang:
                TARGET_LANG[language] ??
                'EN-US',
            }),
          }
        );

        if (!response.ok) throw new Error('Translation failed');
        const data = await response.json();

        setTranslatorResult(
          data.translations[0].text
        );
      } catch {
        setTranslatorResult(
          'Error al traducir. Intenta de nuevo.'
        );
      } finally {
        setIsTranslatorLoading(false);
      }
    };

  const handleTranslatorTTS = () => {
    if (!translatorResult) {
      return;
    }

    void playBotMessageAudio(
      -2,
      translatorResult
    );
  };

  const handleTranslatorSend = () => {
    if (
      !translatorResult ||
      isWaitingForBot
    ) {
      return;
    }

    const text = translatorResult;

    closeTranslator();
    void handleSubmit(text);
  };

  const handleTranslatorDilo = () => {
    if (
      isTranslatorDiloActive &&
      isRecording
    ) {
      setIsTranslatorDiloActive(false);
      closeTranslator();
      void confirmSendAudio();
    } else {
      setIsTranslatorDiloActive(true);
      activateMic();
    }
  };

  const downloadUserAudio = (
    index: number
  ) => {
    const url = getUserAudioUrl(index);

    if (!url) {
      return;
    }

    const anchor =
      document.createElement('a');

    anchor.href = url;
    anchor.download =
      `audio_${index}.mp3`;

    anchor.click();
  };

  const firstVisibleIndex =
    hideFirstMessage
      ? 1
      : 0;

  const lastUserIndex =
    messages.reduce(
      (current, message, index) =>
        message.role === 'user' &&
        index >= firstVisibleIndex
          ? index
          : current,
      -1
    );

  const lastBotIndex =
    messages.reduce(
      (current, message, index) =>
        message.role === 'assistant' &&
        index >= firstVisibleIndex
          ? index
          : current,
      -1
    );


  const ca13FormatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
  };

  const ca13RenderAvatar = () => (
    <div className="ca13-avatar" aria-hidden="true">
      <img src={retziaAvatarV13Url} alt="" />
    </div>
  );

  const ca13AudioControl = (
    index: number,
    onPlay: () => void,
    autoSpeaking = false
  ) => {
    const isManualPlaying = playingIndex === index;
    const isManualPaused = pausedPlaybackIndex === index;

    const handleClick = () => {
      if (autoSpeaking) {
        stopBotSpeaking();
        return;
      }

      if (isManualPlaying) {
        if (pauseCurrentPlayback) {
          pauseCurrentPlayback();
        } else {
          stopCurrentPlayback();
        }
        return;
      }

      if (isManualPaused && resumeCurrentPlayback) {
        resumeCurrentPlayback();
        return;
      }

      onPlay();
    };

    return (
      <button
        type="button"
        className={`ca13-mini-btn ca13-audio-btn${
          isManualPlaying ? ' is-playing' : ''
        }${isManualPaused ? ' is-paused' : ''}`}
        onClick={handleClick}
        title={
          autoSpeaking
            ? 'Detener'
            : isManualPlaying
              ? 'Pausar'
              : isManualPaused
                ? 'Reanudar'
                : 'Escuchar'
        }
        aria-label={
          autoSpeaking
            ? 'Detener audio'
            : isManualPlaying
              ? 'Pausar audio'
              : isManualPaused
                ? 'Reanudar audio'
                : 'Escuchar audio'
        }
      >
        {autoSpeaking ? (
          <Ca13StopIcon />
        ) : isManualPlaying ? (
          <Ca13PauseIcon />
        ) : isManualPaused ? (
          <Ca13PlayIcon />
        ) : (
          <Ca13SpeakerIcon />
        )}
      </button>
    );
  };

  const renderTopicMarkersAfter = (
    index: number
  ) => topicMarkers
    .filter(marker =>
      marker.afterIndex === index + 1
    )
    .map(marker => (
      <div
        key={`topic-${index}-${marker.label}`}
        className="ca13-topic-change"
      >
        <span>
          Tema cambiado: {marker.label}
        </span>
      </div>
    ));

  const ca13LastBotIndex = lastBotIndex;

  return (
      <div
        className={`retzia-workspace-live ca13-root${
          hasTopics ? ' ca13-has-topics' : ''
        }${
          hasTopicRail && showTopics
            ? ' ca13-has-topic-rail'
            : ''
        }${
          mobileInputOpen ? ' ca13-typing' : ''
        }${isRecording ? ' ca13-recording' : ''}${
          pageClassName ? ` ${pageClassName}` : ''
        }`}
      >
        <header
          className={`ca13-header${
            ca13HeaderScrolled ? ' is-scrolled' : ''
          }`}
        >
          <div className="ca13-header-actions">
            {onNewConversation && <button type="button" className="ca13-new-conversation" disabled={newConversationDisabled} onClick={onNewConversation} title={reportMode === 'task_review' ? 'Iniciar una nueva actividad' : 'Iniciar una nueva conversación'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              <span>{newConversationLabel}</span>
            </button>}
            <div className="ca13-report-wrap">
              <ReportProblemDialog
                language={language}
                mode={reportMode}
              />
            </div>

            <button
              type="button"
              className="ca13-header-btn"
              aria-label="Cerrar"
              onClick={() => {
                stopBotSpeaking();
                stopCurrentPlayback();
                onClose();
              }}
            >
              <Ca13CloseIcon size={19} />
            </button>
          </div>
        </header>

        <main
          className="ca13-content"
          onScroll={event =>
            setCa13HeaderScrolled(
              event.currentTarget.scrollTop > 8
            )
          }
        >
          {persistenceError && <p role="alert">{persistenceError}</p>}
          {header}

          <section className="ca13-chat">
            {messages.map((message, index) => {
              if (index < firstVisibleIndex) {
                return null;
              }

              if (message.role === 'assistant') {
                const autoSpeaking =
                  isBotSpeaking &&
                  index === ca13LastBotIndex;

                return (
                  <React.Fragment key={`msg-${index}`}>
                    <div
                      className="ca13-row ca13-row-bot"
                    >
                    {ca13RenderAvatar()}

                    <div className="ca13-bubble-col">
                      <div className="ca13-bubble ca13-bubble-bot">
                        <div className="ca13-text retzia-rich-message">
                          <RetziaRichMessage
                            content={message.content}
                          />
                        </div>

                        <div className="ca13-tools">
                          <button
                            type="button"
                            className="ca13-mini-btn"
                            title="Traducir"
                            aria-label="Traducir"
                            onClick={() =>
                              void handleTranslate(
                                index,
                                message.content
                              )
                            }
                          >
                            <Ca13TranslateIcon />
                          </button>

                          {ca13AudioControl(
                            index,
                            () =>
                              void playBotMessageAudio(
                                index,
                                message.content
                              ),
                            autoSpeaking
                          )}
                        </div>

                        {translatedIndex === index && (
                          <div
                            className="ca13-translation-wrap"
                            ref={translationRef}
                          >
                            <span className="ca13-translation-anchor" />

                            <div className="ca13-translation-card">
                              <div className="ca13-translation-label">
                                <Ca13TranslateIcon />
                                Traducción
                              </div>

                              <button
                                type="button"
                                className="ca13-close-small"
                                aria-label="Cerrar traducción"
                                onClick={() => {
                                  setTranslatedIndex(null);
                                  setTranslationText('');
                                }}
                              >
                                <Ca13CloseIcon size={14} />
                              </button>

                              {isTranslating ? (
                                <span>...</span>
                              ) : (
                                <div className="retzia-rich-message">
                                  <RetziaRichMessage
                                    content={translationText}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    </div>

                    {renderTopicMarkersAfter(index)}
                  </React.Fragment>
                );
              }

              const hasUserAudio =
                userAudioIndices.has(index);

              return (
                <React.Fragment key={`msg-${index}`}>
                  <div
                    className="ca13-row ca13-row-user"
                  >
                  <div className="ca13-bubble ca13-bubble-user">
                    <div
                      className="ca13-text"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {message.content}
                    </div>

                    <div className="ca13-tools ca13-tools-user">
                      {hasUserAudio &&
                        ca13AudioControl(
                          index,
                          () =>
                            playUserMessageAudio(index)
                        )}

                      {hasUserAudio && (
                        <button
                          type="button"
                          className="ca13-mini-btn"
                          title="Descargar audio"
                          aria-label="Descargar audio"
                          onClick={() =>
                            downloadUserAudio(index)
                          }
                        >
                          <Ca13DownloadIcon />
                        </button>
                      )}

                      {index === lastUserIndex && (
                        <button
                          type="button"
                          className="ca13-mini-btn"
                          title="Editar"
                          aria-label="Editar mensaje"
                          onClick={() =>
                            handleStartEdit(
                              message.content
                            )
                          }
                        >
                          <Ca13EditIcon />
                        </button>
                      )}
                    </div>
                  </div>
                  </div>

                  {renderTopicMarkersAfter(index)}
                </React.Fragment>
              );
            })}

            {isRecording && (
              <div className="ca13-row ca13-row-user">
                <div className="ca13-bubble ca13-bubble-user ca13-recording-message-bubble">
                  <div
                    className={`ca13-recording-message-wave${
                      isAudioPaused ? ' is-paused' : ''
                    }`}
                    aria-label={
                      isAudioPaused
                        ? 'Grabación pausada'
                        : 'Grabando'
                    }
                  >
                    <i /><i /><i /><i /><i /><i /><i />
                  </div>
                </div>
              </div>
            )}

            {isWaiting &&
              audioConfirmed &&
              !transcribedThisSession &&
              messages[messages.length - 1]?.role !== 'user' && (
                <div className="ca13-row ca13-row-user">
                  <div className="ca13-bubble ca13-bubble-user ca13-processing-bubble">
                    <div className="ca13-processing">
                      <span className="ca13-spinner" />
                      Procesando tu respuesta…
                    </div>
                  </div>
                </div>
              )}

            {isWaitingForBot && (
              <div className="ca13-row ca13-row-bot ca13-thinking">
                {ca13RenderAvatar()}

                <div className="ca13-bubble ca13-bubble-bot">
                  <div className="ca13-dots" aria-label="RETZIA está pensando">
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}

            {showSuggestion && (
              <div className="ca13-suggestion">
                <div className="ca13-suggestion-head">
                  <div className="ca13-suggestion-icon">
                    <Ca13BulbIcon size={18} />
                  </div>

                  <div className="ca13-suggestion-title">
                    Respuesta sugerida
                  </div>

                  <button
                    type="button"
                    className="ca13-close-small"
                    aria-label="Cerrar sugerencia"
                    onClick={closeSuggestion}
                  >
                    <Ca13CloseIcon size={15} />
                  </button>
                </div>

                <div className="ca13-suggestion-text">
                  {isLoadingSuggestion
                    ? '...'
                    : suggestionText}
                </div>

                <div className="ca13-suggestion-actions">
                  <div className="ca13-suggestion-actions-left">
                    <button
                      type="button"
                      className="ca13-mini-btn"
                      title="Traducir"
                      aria-label="Traducir sugerencia"
                      disabled={isLoadingSuggestion}
                      onClick={() =>
                        void handleSuggestionTranslate()
                      }
                    >
                      <Ca13TranslateIcon />
                    </button>

                    {ca13AudioControl(
                      -1,
                      handleSuggestionPlay
                    )}
                  </div>

                  <button
                    type="button"
                    className="ca13-mini-btn"
                    title="Otra sugerencia"
                    aria-label="Otra sugerencia"
                    disabled={
                      isLoadingSuggestion ||
                      isWaitingForBot
                    }
                    onClick={() =>
                      void handleRefreshSuggestion()
                    }
                  >
                    <Ca13RefreshIcon />
                  </button>
                </div>

                {showSuggestionTranslation && (
                  <div
                    className="ca13-translation-wrap ca13-suggestion-translation"
                    ref={suggestionTranslationRef}
                  >
                    <span className="ca13-translation-anchor" />

                    <div className="ca13-translation-card">
                      <div className="ca13-translation-label">
                        <Ca13TranslateIcon />
                        Traducción
                      </div>

                      <button
                        type="button"
                        className="ca13-close-small"
                        aria-label="Cerrar traducción"
                        onClick={() => {
                          setShowSuggestionTranslation(false);
                          setSuggestionTranslation('');
                        }}
                      >
                        <Ca13CloseIcon size={14} />
                      </button>

                      {isTranslatingSuggestion
                        ? '...'
                        : suggestionTranslation}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div ref={messagesEndRef} />
          </section>

          {afterConversation}
        </main>

        {errorAppeared && (
          <div className="ca13-error-banner">
            Ocurrió un error, favor de volver a intentarlo.
          </div>
        )}

        <div className="ca13-dock-wrap">
          {isEditing && (
            <div className="ca13-edit-banner">
              <Ca13EditIcon />

              <div className="ca13-edit-copy">
                <span className="ca13-edit-label">
                  Corregir mensaje
                </span>
                <span className="ca13-edit-text">
                  {editingMessageText}
                </span>
              </div>

              <button
                type="button"
                className="ca13-close-small"
                aria-label="Cancelar edición"
                onClick={() => {
                  setIsEditing(false);
                  setEditingMessageText('');
                  handleChange({
                    target: { value: '' },
                  } as React.ChangeEvent<HTMLInputElement>);
                  setMobileInputOpen(false);
                }}
              >
                <Ca13CloseIcon size={14} />
              </button>
            </div>
          )}

          {isRecording ? (
            <div className="ca13-dock ca13-recording-dock">
              <button
                type="button"
                className="ca13-recording-cancel"
                aria-label="Cancelar grabación"
                onClick={() => void cancelAudioFlow()}
              >
                <Ca13TrashIcon />
              </button>

              <div className="ca13-recording-pill">
                <div className="ca13-recording-meta">
                  <div
                    className={`ca13-wave${
                      isAudioPaused ? ' is-paused' : ''
                    }`}
                  >
                    <i /><i /><i /><i /><i />
                  </div>

                  <div className="ca13-recording-copy">
                    <strong>
                      {isAudioPaused
                        ? 'Pausado'
                        : 'Escuchando…'}
                    </strong>
                    <span>
                      {ca13FormatTime(
                        ca13RecordingSeconds
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="ca13-recording-pause"
                aria-label={
                  isAudioPaused
                    ? 'Reanudar grabación'
                    : 'Pausar grabación'
                }
                onClick={
                  isAudioPaused
                    ? resumeAudioPaused
                    : () => void pauseAudio()
                }
              >
                <Ca13RecordingPauseIcon
                  paused={isAudioPaused}
                />
              </button>

              <button
                type="button"
                className="ca13-mic-btn ca13-recording-send"
                aria-label="Enviar audio"
                onClick={handleConfirmAudio}
              >
                <Ca13ArrowIcon />
              </button>
            </div>
          ) : (
            <div className="ca13-dock">
              <button
                type="button"
                className="ca13-mobile-back"
                aria-label="Volver a herramientas"
                onClick={() => {
                  setMobileInputOpen(false);
                  inputRef.current?.blur();
                }}
              >
                <Ca13BackIcon />
              </button>

              <button
                type="button"
                className="ca13-dock-btn ca13-keyboard-btn"
                aria-label="Abrir teclado"
                onClick={openMobileInput}
              >
                <Ca13KeyboardIcon />
              </button>

              <div className="ca13-composer">
                <input
                  ref={inputRef}
                  value={input}
                  placeholder={inputPlaceholder}
                  onChange={handleChange}
                  onKeyDown={event => {
                    if (
                      event.key === 'Enter' &&
                      !event.shiftKey
                    ) {
                      event.preventDefault();
                      handleSend();
                    }
                  }}
                />

                <button
                  type="button"
                  className="ca13-send"
                  aria-label="Enviar"
                  disabled={
                    !showSendIcon ||
                    isWaitingForBot
                  }
                  onClick={handleSend}
                >
                  <Ca13SendIcon />
                </button>
              </div>

              <button
                type="button"
                className={`ca13-dock-btn ca13-hint-btn${
                  bulbIsLit ? ' is-pulsing' : ''
                }`}
                aria-label="Focus"
                title="Focus"
                disabled={isWaitingForBot}
                onClick={() =>
                  void fetchAndShowSuggestion()
                }
              >
                <Ca13BulbIcon />
              </button>

              {hasTopics && (
                <button
                  type="button"
                  className={`ca13-dock-btn ca13-topics-btn${
                    showTopics ? ' is-active' : ''
                  }`}
                  aria-label="Cambiar tema"
                  aria-expanded={showTopics}
                  title="Temas"
                  disabled={isWaitingForBot}
                  onClick={toggleTopics}
                >
                  <Ca13HashIcon />
                </button>
              )}

              <button
                type="button"
                className="ca13-dock-btn ca13-world-btn"
                aria-label="Traductor"
                title="Traductor"
                disabled={isWaitingForBot}
                onClick={openTranslator}
              >
                <Ca13GlobeIcon />
              </button>

              <span className="ca13-mobile-spacer" aria-hidden="true" />

              <button
                type="button"
                className="ca13-mic-btn"
                aria-label="Hablar"
                disabled={isWaitingForBot}
                onClick={activateMic}
              >
                <Ca13MicIcon />
              </button>
            </div>
          )}

          {hasTopicRail &&
            showTopics &&
            !isRecording &&
            !mobileInputOpen && (
              <div
                className="ca13-topic-rail"
                aria-label="Temas de conversación"
              >
                <div className="ca13-topic-rail-scroll">
                  {topics.map(topic => (
                    <button
                      key={topic}
                      type="button"
                      className="ca13-topic-rail-pill"
                      disabled={isWaitingForBot}
                      onClick={() =>
                        handleTopicSelect(topic)
                      }
                    >
                      {topic}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="ca13-topic-rail-close"
                  aria-label="Cerrar temas"
                  onClick={closeTopics}
                >
                  <Ca13CloseIcon />
                </button>
              </div>
            )}
        </div>

        {showTranslator && (
          <div
            className="ca13-sheet-backdrop"
            role="presentation"
            onMouseDown={event => {
              if (event.target === event.currentTarget) {
                closeTranslator();
              }
            }}
          >
            <div
              className="ca13-translator-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={`Cómo decir en ${
                LANG_LABEL[language] ?? 'inglés'
              }`}
            >
              <div className="ca13-sheet-head">
                <Ca13GlobeIcon size={20} />
                <span>
                  Cómo decir en{' '}
                  {LANG_LABEL[language] ?? 'inglés'}
                </span>
                <span className="ca13-sheet-spacer" />

                <button
                  type="button"
                  className="ca13-close-small"
                  aria-label="Cerrar traductor"
                  onClick={closeTranslator}
                >
                  <Ca13CloseIcon />
                </button>
              </div>

              <div className="ca13-sheet-body">
                <div className="ca13-field-label">
                  Escribe en español
                </div>

                <div className="ca13-translate-entry">
                  <textarea
                    ref={ca13TranslatorInputRef}
                    value={translatorInput}
                    placeholder="Escribe aquí lo que quieres decir…"
                    onChange={event =>
                      setTranslatorInput(
                        event.target.value
                      )
                    }
                    onKeyDown={event => {
                      if (
                        event.key === 'Enter' &&
                        !event.shiftKey
                      ) {
                        event.preventDefault();
                        void handleTranslatorSubmit();
                      }
                    }}
                  />

                  <button
                    type="button"
                    className="ca13-translate-now"
                    aria-label="Traducir"
                    disabled={
                      !translatorInput.trim() ||
                      isTranslatorLoading
                    }
                    onClick={() =>
                      void handleTranslatorSubmit()
                    }
                  >
                    <Ca13ArrowIcon />
                  </button>
                </div>

                {(isTranslatorLoading || translatorResult) && (
                  <div className="ca13-result-block">
                    <div className="ca13-field-label">
                      En {LANG_LABEL[language] ?? 'inglés'}
                    </div>

                    <div className="ca13-translate-result">
                      {isTranslatorLoading
                        ? '...'
                        : translatorResult}
                    </div>

                    {isTranslatorDiloActive &&
                    (isRecording || isWaiting) ? (
                      <div className="ca13-dilo-panel">
                        <div className="ca13-dilo-top">
                          <div className="ca13-dilo-listening">
                            <span className="ca13-spinner" />
                            {isRecording
                              ? 'Escuchando…'
                              : 'Procesando…'}
                          </div>

                          <button
                            type="button"
                            className="ca13-close-small"
                            aria-label="Cancelar Dilo"
                            onClick={() => {
                              setIsTranslatorDiloActive(false);

                              if (isRecording) {
                                void cancelAudioFlow();
                              } else {
                                cancelMicFlow();
                              }
                            }}
                          >
                            <Ca13CloseIcon size={14} />
                          </button>
                        </div>

                        {isRecording && (
                          <div className="ca13-dilo-actions">
                            <button
                              type="button"
                              className="ca13-sheet-action"
                              onClick={
                                isAudioPaused
                                  ? resumeAudioPaused
                                  : () => void pauseAudio()
                              }
                            >
                              {isAudioPaused
                                ? 'Reanudar'
                                : 'Pausar'}
                            </button>

                            <button
                              type="button"
                              className="ca13-sheet-action is-primary"
                              onClick={handleTranslatorDilo}
                            >
                              Enviar
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="ca13-sheet-actions">
                        <button
                          type="button"
                          className="ca13-sheet-action"
                          onClick={() => {
                            if (playingIndex === -2) {
                              if (pauseCurrentPlayback) {
                                pauseCurrentPlayback();
                              } else {
                                stopCurrentPlayback();
                              }
                            } else if (
                              pausedPlaybackIndex === -2 &&
                              resumeCurrentPlayback
                            ) {
                              resumeCurrentPlayback();
                            } else {
                              handleTranslatorTTS();
                            }
                          }}
                          disabled={!translatorResult}
                        >
                          {playingIndex === -2 ? (
                            <Ca13PauseIcon />
                          ) : pausedPlaybackIndex === -2 ? (
                            <Ca13PlayIcon />
                          ) : (
                            <Ca13SpeakerIcon />
                          )}
                          Escuchar
                        </button>

                        <button
                          type="button"
                          className="ca13-sheet-action"
                          disabled={isWaitingForBot}
                          onClick={handleTranslatorDilo}
                        >
                          Dilo
                        </button>

                        <button
                          type="button"
                          className="ca13-sheet-action is-primary"
                          disabled={
                            !translatorResult ||
                            isWaitingForBot
                          }
                          onClick={handleTranslatorSend}
                        >
                          Enviar
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showTopics && hasTopicSheet && (
          <div
            className="ca13-sheet-backdrop"
            role="presentation"
            onMouseDown={event => {
              if (event.target === event.currentTarget) {
                closeTopics();
              }
            }}
          >
            <div
              className="ca13-translator-sheet ca13-topics-sheet"
              role="dialog"
              aria-modal="true"
              aria-label="Cambiar tema"
            >
              <div className="ca13-sheet-head">
                <Ca13HashIcon size={20} />
                <span>Elige un tema</span>
                <span className="ca13-sheet-spacer" />

                <button
                  type="button"
                  className="ca13-close-small"
                  aria-label="Cerrar temas"
                  onClick={closeTopics}
                >
                  <Ca13CloseIcon />
                </button>
              </div>

              <div className="ca13-sheet-body">
                <div className="ca13-topics-grid">
                  {topics.map(topic => (
                    <button
                      key={topic}
                      type="button"
                      className="ca13-topic-pill"
                      onClick={() =>
                        handleTopicSelect(topic)
                      }
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}

export default RetziaActivityWorkspace;
